/**
 * Where everything sits on the PHONE activity map (the octopus) — the phone frame of Figma
 * `G2e3uWY8iBZd4g0pFJ5ruk`, node 2046:117940 (375×812; header 2046:118326, board 2046:118353). Every number below
 * is in that frame's SCREEN coordinates. The trays never expand: their "+N" opens the folded card instead (the
 * user's decision, superseding the expanded-tray frame 2048:122252).
 *
 * ## A 1:1 port, not a web design
 *
 * This is iOS `ActivityMapLayout.swift` / Android `ActivityMapLayout.kt` in TypeScript — the same names
 * (camelCase, as Swift spells them), the same numbers, the same arithmetic — so mobile web draws the octopus
 * exactly as both phones do. The three are pinned to ONE set of expectations,
 * `native-apps/shared/activity-map-layout/contract.json`; change a number here and in both natives together,
 * and add the case there, never to one surface's test alone.
 *
 * Every piece of the phone octopus is ported: the trays (the tools tray ending in its fixed "+"), the rail (a tap opens the category's page, on the phones
 * too), the cards. The motion is its own model on all three, `phone-flow.ts` (pinned to
 * `native-apps/shared/activity-map-flow/contract.json`), built on this file's `*OnScreen` helpers.
 *
 * Pure and import-free on purpose: the unit tests read it without dragging the app's graph in (see the
 * package CLAUDE.md "Tests"). The rail inks are design-token NAMES (CSS custom properties), not colours, so
 * the token guard sees no literal and dark mode can flip them later.
 *
 *     ┌─────────────────────────── divider ───────────────────────────┐
 *     │  12 new updates                                       ● Brief │
 *     │  since your last visit                                ● Tasks │
 *     │   ┌── channels ──┐                                    ● …     │
 *     │   └──────┬───────┘        ╭──────╮                    ● Files │
 *     │          ╰────────────────│ ▦ )  ) ─── wires ─────────╯       │
 *     │   ┌── connectors ─┐       ╰──────╯                            │
 *     └───────────────────────── Ask Maestro ─────────────────────────┘
 */

// ── the design frame — every constant below is in these units

export const frameW = 375;
export const frameH = 812;

/** The map band: from the header's divider down to the top of the composer. */
export const bandTop = 202;
export const bandBottom = 709;

// ── the header (Figma 2046:118326)

/**
 * The view switch (Figma "Symphony Icon Button mobile" pair): a 76×40 pill holding two 32 circular buttons, 4 in
 * from its edge and 4 apart; the selected one is white at `viewSwitchSelectedAlpha`. Web draws it in
 * `Home3Body.module.css` (`.viewSwitchNarrow`); these are the numbers it is checked against.
 */
export const viewSwitchW = 76;
export const viewSwitchH = 40;
export const viewSwitchRadius = 26;
export const viewSwitchPad = 4;
export const viewSwitchGap = 4;
export const viewSwitchButton = 32;
export const viewSwitchSelectedAlpha = 0.69;

// ── the headline

/** "12 new updates / since your last visit", Archivo 26/27, over the map's empty top-left quarter. */
export const headlineX = 24;
export const headlineY = 224;
export const headlineW = 235;
export const headlineSize = 26;
export const headlineLine = 27;

// ── the Live Updates chip and its sheet (Figma 2046:117940, 2048:119655)

/** "● Live Updates" under the headline: a black 26-high pill, 5 round, its 8 neon dot 12 in and the label 7 after it. */
export const liveChipX = 24;
/** Where the chip sits under a TWO-line headline (224 + 2 × 27 + `liveChipGap`). It follows the headline's real last
 *  line, so a headline that wraps further (a longer language, a wider font) pushes it down instead of under it. */
export const liveChipY = 290;
export const liveChipGap = 12;
export const liveChipH = 26;
export const liveChipRadius = 5;
export const liveChipPad = 12;
export const liveDot = 8;
export const liveDotGap = 7;
export const liveChipTextSize = 12;
/** The Recent activity sheet's top edge — under the island row, over the headline — and its corners. */
export const liveSheetTop = 224;
export const liveSheetRadius = 24;
/** The chip's dot blinks: opacity 1 → 0.25 → 1, forever (the prototype's `[data-livedot]`). */
export const liveDotBlinkMs = 1500;

// ── the sheet's live feed (the prototype's): on every open the newest rows ARRIVE, one at a time

/** How many of the newest rows arrive; the older ones are already there. */
export const liveFeedArrivals = 3;
/** The first arrival, once the sheet's slide has settled. */
export const liveFeedStartMs = 300;
/** An arriving row's three dots, before its text types in… */
export const liveFeedDotsMs = 700;
/** …at this many characters a second; its link shows once the text is in. */
export const liveFeedCps = 34;
/** The next row arrives once nothing is typing, and no sooner than this after the last one came. */
export const liveFeedGapMs = 1400;
/** The list slides up by the arrival over this long (cubic-bezier(.2,.8,.2,1)) — desktop's activity panel's own timing,
 *  as the row fades in over `liveFeedFadeMs`, `liveFeedFadeDelayMs` in. */
export const liveFeedSlideMs = 560;
export const liveFeedFadeMs = 480;
export const liveFeedFadeDelayMs = 120;
/** A row's link — always laid out, so the row never grows — fades in over this long once its text is in. */
export const liveFeedLinkFadeMs = 200;

/** How many rows of `count` arrive when the sheet opens. */
export function liveFeedPending(count: number): number {
  return Math.max(0, Math.min(liveFeedArrivals, count));
}

/** How much of an arriving row's text shows `elapsedMs` after it arrived: null while its dots show, then the
 *  characters typed so far, never past the text. */
export function liveFeedTyped(text: string, elapsedMs: number): number | null {
  if (elapsedMs < liveFeedDotsMs) return null;
  return Math.min(text.length, Math.floor(((elapsedMs - liveFeedDotsMs) / 1000) * liveFeedCps));
}

// ── the orbit

export const ringCx = 131;
export const ringCy = 509;
export const ringR = 90;
/** The dashed ring is drawn 2-on/2-off, like the Figma ellipse. */
export const ringDash: readonly number[] = [2, 2];

/** One agent per dot, clockwise from the top. Beyond this the roster is truncated — the ring is a glance at the
 *  team, and a dot you cannot tell from its neighbour is worse than no dot. */
export const maxAgentDots = 7;
export const agentDot = 30;
/** The Maestro's plate at the centre of the ring (Figma 888-135033). */
export const maestroSize = 43;
export const maestroRadius = 14;

/** The seven dots exactly where the phone frame (Figma 2046:118353) places them. */
export const sevenAgentAngles: readonly number[] = [3, 60, 111, 163, 212, 261, 309];

/** Any other count is an even spread nudged by a fixed, repeating jitter, so the ring still reads as a living
 *  cluster rather than a clock face and the same roster always draws the same ring. */
const jitter: readonly number[] = [0, 8, -6, 5, -9, 4, -3];

/** The angles the agent dots sit at, clockwise from the top (0° = 12 o'clock). */
export function agentAngles(count: number): number[] {
  if (count <= 0) return [];
  if (count === sevenAgentAngles.length) return [...sevenAgentAngles];
  const step = 360 / count;
  return Array.from({ length: count }, (_, i) => (i * step + jitter[i % jitter.length] + 360) % 360);
}

/** A point on the ring at `angleDeg`, measured clockwise from the top. */
export function ringPoint(angleDeg: number, radius: number = ringR): Pt {
  const a = ((angleDeg - 90) * Math.PI) / 180;
  return { x: ringCx + Math.cos(a) * radius, y: ringCy + Math.sin(a) * radius };
}

// ── the right rail

/** The rail's seven destinations, top to bottom. `messages` is the activity view's Outreach category. */
export type Rail = 'brief' | 'tasks' | 'routines' | 'goals' | 'messages' | 'apps' | 'files';
export const rails: readonly Rail[] = ['brief', 'tasks', 'routines', 'goals', 'messages', 'apps', 'files'];

/**
 * The wire's ink per rail: the Figma frame's exact colours, the same in light and dark (the user's call, 2026-11-02) —
 * `--home-map-rail-*` in `globals.css`, the values both phones carry: brief #D6FF59, tasks #ADFC7F (its dot #A4FF70),
 * routines #F6C0F3, goals #CF999D, messages #96F4FF, apps #B1C2FF, files #FFBD9E.
 */
export const railWireInk: Readonly<Record<Rail, string>> = {
  brief: 'var(--home-map-rail-brief)',
  tasks: 'var(--home-map-rail-tasks)',
  routines: 'var(--home-map-rail-routines)',
  goals: 'var(--home-map-rail-goals)',
  messages: 'var(--home-map-rail-messages)',
  apps: 'var(--home-map-rail-apps)',
  files: 'var(--home-map-rail-files)',
};
/** The end dot's ink — the wire's own, except Tasks, whose dot is the brand lime #A4FF70 (its wire is #ADFC7F). */
export const railDotInk: Readonly<Record<Rail, string>> = { ...railWireInk, tasks: 'var(--home-map-rail-tasks-dot)' };

/** A tray's wire and its anchor dot — the frame's Border/light/Active grey, which IS a token on web. */
export const clusterWireInk = 'var(--ds-border-light-active)';

/** The hairline between the island row and the octopus (Figma 2046:118352 "Line 561"): edge to edge, 1pt,
 *  Foundation neutral-500 at this alpha. Web draws it from the `--home-map-divider` role (`globals.css`). */
export const mapDividerAlpha = 0.2;

/** Centre-x of the rail's art, and of the 8pt dot where each wire lands just left of it. */
export const railIconCx = 326;
export const wireEndCx = 301;
export const wireDot = 8;
/** A rail wire's stroke, drawn with round caps. */
export const wireStroke = 2;
/** Clear air between the orbit and where a wire begins. */
export const wireRingGapDefault = 7;
/** The rail's art box — the activity tile's own 40 drawing. */
export const railIconSize = 40;
/** The label sits directly under the art box. */
export const railLabelGap = 0;
/** The brief's document plate inside the art box: white, 29 across, corner 8. */
export const briefPlate = 29;
export const briefPlateCorner = 8;
/** The count disc: 20 across, its centre 17 right of and 15 above the art box's centre. */
export const railBadgeSize = 20;
export const railBadgeDx = 17;
export const railBadgeDy = -15;
/** The rail column's own width (the Figma rail frame). Contents are centred in it, so a long label can never
 *  drag the icon off the design's centre line and leave a wire landing beside its mark. */
export const railColumnW = 69;

/** Each rail row's art centre, top to bottom — the frame spaces them unevenly, so they are its own. */
export const railIconCys: readonly number[] = [269, 335, 401, 465, 533, 605, 671];

/** Where each row's wire lands — not always level with its art (the frame draws the brief's a little higher and
 *  the files' a little lower). */
export const wireEndCys: readonly number[] = [261, 335, 395, 462, 531, 607, 682];

/** The bearing each row's wire leaves the orbit at, clockwise from the top: the frame fans the seven out of the
 *  ring's upper-right quarter in the order they land, not each straight at its row. */
export const wireStartAngles: readonly number[] = [30, 42, 57, 74, 87, 111, 133];

const clampIndex = (index: number, list: readonly number[]) => Math.max(0, Math.min(index, list.length - 1));

/** Vertical centre of rail row `index`'s art. */
export function railCy(index: number): number {
  return railIconCys[clampIndex(index, railIconCys)];
}

/** Where rail row `index`'s wire lands. */
export function wireEndCy(index: number): number {
  return wireEndCys[clampIndex(index, wireEndCys)];
}

/** The bearing rail row `index`'s wire leaves the orbit at. */
export function wireStartAngle(index: number): number {
  return wireStartAngles[clampIndex(index, wireStartAngles)];
}

/** How far either side of an agent, in degrees, a wire starts giving it room. A dot is `agentDot` across centred
 *  ON the ring, so a wire leaving at the plain gap would start inside one. */
export const agentWireClearZone = 26;

/** The shortest angular distance between two bearings, in degrees (0..180). */
export function angularGap(a: number, b: number): number {
  const d = Math.abs((a - b) % 360);
  return Math.min(d, 360 - d);
}

/**
 * How far outside the ring a wire leaving at `angle` must start, given where the agents stand.
 *
 * Normally the plain `wireRingGapDefault`. Where an agent is standing it opens up to clear the whole dot, eased
 * across `agentWireClearZone` (smoothstep) so neighbouring wires fan gently. (Swift names the constant and the
 * function both `wireRingGap`; TypeScript cannot, so the constant carries `Default`.)
 */
export function wireRingGap(angle: number, agents: number): number {
  const angles = agentAngles(agents);
  if (angles.length === 0) return wireRingGapDefault;
  const nearest = Math.min(...angles.map((a) => angularGap(angle, a)));
  if (nearest >= agentWireClearZone) return wireRingGapDefault;
  const clear = agentDot / 2 + wireRingGapDefault;
  const t = nearest / agentWireClearZone; // 0 at the dot's centre, 1 at the zone's edge
  return wireRingGapDefault + (clear - wireRingGapDefault) * (1 - t * t * (3 - 2 * t));
}

/**
 * The wire from the orbit out to rail row `index`, as one cubic Bézier: it leaves the ring at the frame's own
 * bearing for its row, starting OUTSIDE the orbit (clear of any agent standing there), and arrives horizontally
 * at the centre of its dot, which is what makes the seven read as a bundle.
 */
export function wire(index: number, agents = 0): Curve {
  const end = { x: wireEndCx, y: wireEndCy(index) };
  const angle = wireStartAngle(index);
  const start = ringPoint(angle, ringR + wireRingGap(angle, agents));
  const dx = end.x - start.x;
  return { start, c1: { x: start.x + dx * 0.55, y: start.y }, c2: { x: start.x + dx * 0.45, y: end.y }, end };
}

// ── the two chip clusters

/** A cluster is a rounded tray of round chips, joined to the orbit by a thin wire and an anchor dot. */
export type Cluster = 'channels' | 'connectors';
export const clusters: readonly Cluster[] = ['channels', 'connectors'];

export const chip = 32;
export const chipGap = 2;
export const clusterPad = 6;
export const channelsRadius = 20;
export const connectorsRadius = 36;
/** A tray's height: one chip and its padding. */
export const trayH = chip + clusterPad * 2;
/** Left edge and vertical centre of each tray, folded (Figma 2046:118353). */
export const channelsX = 48.4;
export const channelsCy = 375;
export const connectorsX = 83;
export const connectorsCy = 672;
/** The connectors tray's wire lands on its TOP edge at this x (a short tray pulls it in — `clusterAnchor`). */
export const connectorsAnchorX = 157;

/** How much of the tray's neutral shows through. */
export const trayAlpha = 0.6;

/** The grey dot where a cluster's wire meets its tray, and the white ring inside its edge. */
export const anchorDot = 11.5;
export const anchorDotRing = 2;
/** A tray wire's stroke. */
export const clusterWireStroke = 1;

/** The bearings each tray's wire meets the orbit at: the channels' from above-left, arriving straight down, and
 *  the connectors' from the ring's foot, leaving sideways. */
export const channelsRingAngle = 326;
export const connectorsRingAngle = 187;

/** How many chips a tray shows before the rest fold into one "+N". */
export const maxChips = 3;

/**
 * What a tray draws: `maxChips` chips and then a "+N" for whatever is left. The "+N" sits BESIDE the three, it
 * does not take one of their places (the designer's note on Figma 888-132954, where seven connectors draw as three
 * chips and "+4"). A tray never expands: the "+N" opens the folded card, which lists `hiddenChips(items)`.
 *
 * The TOOLS tray then ends in its "+" (`hasAddButton`), which is not a chip and is never folded.
 *
 * So the composition always fits the frame: the widest channels tray is three chips and a "+N" (146, ending at
 * 48.4 + 146), the widest tools tray those and the "+" (180, x 83…263) — short of the rail's wire dots (297) and
 * its column (291.5), and the Files wire passes at least 6 clear of that tray's rounded end.
 */
export function visibleChips<T>(items: readonly T[]): Fold<T> {
  if (items.length <= maxChips) return { shown: [...items], more: 0, hasMore: false };
  const kept = items.slice(0, maxChips);
  const more = items.length - kept.length;
  return { shown: kept, more, hasMore: more > 0 };
}

/** What a tray's "+N" hides: the fold's tail past the first `maxChips`, in tray order — what the folded card lists. */
export function hiddenChips<T>(items: readonly T[]): T[] {
  return items.slice(maxChips);
}

/**
 * The TOOLS tray always ends in a "+" — after its chips and its "+N", and alone when no tool is connected — that
 * opens the "Connect more" card (the desktop canvas's connect-more node, the tools row's fixed last slot). The
 * channels tray has none.
 */
export function hasAddButton(cluster: Cluster): boolean {
  return cluster === 'connectors';
}

/** The tray's own width — every round button in it: the chips, the "+N" and the "+" (one more chip slot). */
export function trayWidth(chipCount: number, hasMore: boolean, hasAdd = false): number {
  const n = chipCount + (hasMore ? 1 : 0) + (hasAdd ? 1 : 0);
  if (n <= 0) return 0;
  return clusterPad * 2 + chip * n + chipGap * (n - 1);
}

/** A tray's left edge — each tray holds its own and grows right with its chips. */
export function trayX(cluster: Cluster): number {
  return cluster === 'channels' ? channelsX : connectorsX;
}

/**
 * Where a tray's wire leaves it — the anchor dot, centred on the tray's edge. The channels tray is tapped on its
 * LEFT edge at mid-height, the connectors tray on its TOP edge at `connectorsAnchorX`.
 *
 * The connectors tray passes its width as `foldedConnectorsWidth` — the WHOLE tray, its "+" included — and the dot
 * sits on the centre of the tray's last round button or at x 157, whichever is smaller:
 * `min(157, connectorsX + width − clusterPad − chip / 2)`. The "+" counts as that last button, so the wire always
 * meets a real item: no tools (44) → on the "+" at 105; one (78) → on the "+" at 139; two or more (112+) → 157.
 * With no width it is the plain anchor.
 */
export function clusterAnchor(cluster: Cluster, foldedConnectorsWidth?: number | null): Pt {
  if (cluster === 'channels') return { x: channelsX, y: channelsCy };
  let x = connectorsAnchorX;
  if (foldedConnectorsWidth != null && foldedConnectorsWidth > 0) {
    x = Math.min(x, connectorsX + foldedConnectorsWidth - clusterPad - chip / 2);
  }
  return { x, y: connectorsCy - trayH / 2 };
}

/** The bearing a tray's wire meets the orbit at. */
export function clusterRingAngle(cluster: Cluster): number {
  return cluster === 'channels' ? channelsRingAngle : connectorsRingAngle;
}

/**
 * The wire from a tray's anchor dot to the orbit, authored tray→orbit, in the frame's two shapes: CHANNELS swings
 * out left and down from the tray's edge and arrives at the orbit going straight DOWN onto it; CONNECTORS leaves
 * the tray's top SIDEWAYS (to the left) and meets the orbit's foot sideways too, hooking round under the ring.
 * Each reach is a share of the wire's own drop, so the S keeps its shape on any band.
 */
export function clusterCurve(cluster: Cluster, anchor: Pt, onRing: Pt): Curve {
  const drop = Math.abs(onRing.y - anchor.y);
  if (cluster === 'channels') {
    return {
      start: anchor,
      c1: { x: anchor.x - drop * 0.48, y: anchor.y + drop * 0.41 },
      c2: { x: onRing.x, y: onRing.y - drop * 0.56 },
      end: onRing,
    };
  }
  return {
    start: anchor,
    c1: { x: anchor.x - drop * 0.6, y: anchor.y },
    c2: { x: onRing.x + drop * 0.75, y: onRing.y },
    end: onRing,
  };
}

/** The wire from a tray's anchor dot to the orbit. It stops OUTSIDE the ring — further out where an agent
 *  stands — exactly as a rail wire leaves clear of one. */
export function clusterWire(cluster: Cluster, agents = 0, foldedConnectorsWidth?: number | null): Curve {
  const angle = clusterRingAngle(cluster);
  const radius = ringR + wireRingGap(angle, agents);
  return clusterCurve(cluster, clusterAnchor(cluster, foldedConnectorsWidth), ringPoint(angle, radius));
}

// ── the map's cards (Figma 2048:122213 agent, 2048:125261 connector)

/** The card a dot or a chip opens: 265 wide, 20 padding, corner 16, CENTRED on the screen (not anchored to what
 *  was tapped — the user's rule for every popover on the map), closed by ✕ or a press outside. */
export const mapCardW = 265;
export const mapCardPad = 20;
export const mapCardRadius = 16;
/** The header row (mark + name) → divider → description, 14 apart; the action button 16 under them. */
export const mapCardGap = 14;
export const mapCardActionGap = 16;
/** The agent's dot beside its name, and the connector's round chip with its logo inside. */
export const mapCardMark = 20;
export const mapCardChip = 28;
export const mapCardMarkGap = 6;
/** The agent's role sits 4 under the mark + name row. */
export const mapCardRoleGap = 4;
/** The ✕: a 42 ghost icon button holding an 18 glyph, 6 in from the card's top-right corner. */
export const mapCardClose = 42;
export const mapCardCloseIcon = 18;
export const mapCardCloseInset = 6;
/** The outlined full-width action ("View {name}", "Connect", "Manage") — the medium button's 36. */
export const mapCardButtonH = 36;

/**
 * The FOLDED card a tray's "+N" opens (the desktop canvas's folded card, `MapHome`'s `n.folded`): the "+N" plate
 * and "{count} more", then the hidden chips one row each — the chip's mark in a `mapCardChip` round chip,
 * `mapCardListNameGap` before its name — `mapCardListRowGap` apart, and "Manage". At most `mapCardListMaxRows`
 * rows (`foldedRows`).
 */
export const mapCardListRowGap = 8;
export const mapCardListNameGap = 8;
export const mapCardListMaxRows = 6;

/**
 * How the folded card lists `hiddenCount` chips: every one while they fit in `mapCardListMaxRows` rows; past it,
 * one row fewer and a last "{more} more" row, so the card's height holds however many are folded.
 * 6 → 6 shown, 0 more; 7 → 5 shown, 2 more; 0 → nothing.
 */
export function foldedRows(hiddenCount: number): { shown: number; more: number } {
  const hidden = Math.max(0, hiddenCount);
  if (hidden <= mapCardListMaxRows) return { shown: hidden, more: 0 };
  const shown = mapCardListMaxRows - 1;
  return { shown, more: hidden - shown };
}

/**
 * Where a CHANNELS chip's tap goes — the phones' decision, 1:1. A reader with no phone is sent to set the number
 * up before WhatsApp or SMS can work; with one, WhatsApp opens and SMS composes to Symphony's number; every other
 * channel lands on the Connectors screen's Channels tab, on its own section where one exists.
 */
export type ChannelRoute =
  | { kind: 'managePhone' }
  | { kind: 'whatsapp' }
  | { kind: 'smsOut' }
  | { kind: 'channels'; target?: string };

export function channelChipRoute(slug: string | undefined, hasPhone: boolean): ChannelRoute {
  if (slug === 'whatsapp') return hasPhone ? { kind: 'whatsapp' } : { kind: 'managePhone' };
  if (slug === 'sms-in') return hasPhone ? { kind: 'smsOut' } : { kind: 'managePhone' };
  if (slug === 'email-in') return { kind: 'channels' };
  if (slug === 'web' || slug === 'phone-app') return { kind: 'channels', target: 'symphony-apps' };
  if (slug?.startsWith('coder:')) return { kind: 'channels', target: 'ai-coders' };
  return { kind: 'channels' };
}

/**
 * Whether a chip's card offers "Manage" (connected) or "Connect". A tool is as connected as its chip says. A
 * channel is too — except WhatsApp and SMS while the reader has no phone, where the chip's own route goes to set
 * the number up first (`channelChipRoute` → `managePhone`).
 */
export function chipConnected(cluster: Cluster, slug: string | undefined, connected: boolean, hasPhone: boolean): boolean {
  if (!connected) return false;
  if (cluster !== 'channels') return true;
  return channelChipRoute(slug, hasPhone).kind !== 'managePhone';
}

/**
 * Where a CHANNELS chip goes on the Connectors screen's Channels tab — the web reading of the phones'
 * `channelChipRoute`. The phones open WhatsApp or an SMS compose when the reader has a number; a browser has
 * neither app to hand off to, so every chip lands on its own section of the Channels tab (`undefined` = the top
 * of the tab), the same sections the phones' `Channels(target)` branch names.
 */
export function channelChipTarget(slug: string | undefined): string | undefined {
  if (slug === 'whatsapp') return 'whatsapp';
  if (slug === 'web' || slug === 'phone-app') return 'symphony-apps';
  if (slug?.startsWith('coder:')) return 'ai-coders';
  return undefined;
}

// ── mapping the frame onto a real screen

export interface Pt {
  x: number;
  y: number;
}

export interface Curve {
  start: Pt;
  c1: Pt;
  c2: Pt;
  end: Pt;
}

export interface Fold<T> {
  shown: T[];
  more: number;
  hasMore: boolean;
}

/**
 * Frame units → screen points. `y` is measured from `bandTop`, so the band's own top is 0; `x` from the frame's own
 * x 0 — nothing on the map ever reaches past either side of the frame.
 */
export class Scale {
  constructor(
    readonly sx: number,
    readonly sy: number,
    readonly originY: number,
  ) {}

  x(v: number): number {
    return v * this.sx;
  }

  y(v: number): number {
    return (v - this.originY) * this.sy;
  }

  pt(p: Pt): Pt {
    return { x: this.x(p.x), y: this.y(p.y) };
  }

  /** Sizes follow the WIDTH scale alone, so a dot stays round on a tall screen. */
  size(v: number): number {
    return v * this.sx;
  }

  /** The orbit's centre and radius IN SCREEN POINTS — a true circle, scaled uniformly like every other size. */
  ringCentre(): Pt {
    return { x: this.x(ringCx), y: this.y(ringCy) };
  }

  ringRadius(): number {
    return this.size(ringR);
  }

  /** A point on the on-screen orbit at `angleDeg`, measured clockwise from the top. */
  ringPointOnScreen(angleDeg: number, radius: number = ringR): Pt {
    const a = ((angleDeg - 90) * Math.PI) / 180;
    const c = this.ringCentre();
    return { x: c.x + Math.cos(a) * this.size(radius), y: c.y + Math.sin(a) * this.size(radius) };
  }

  /** A rail wire in SCREEN points: it must leave the on-screen circle, so it is not the frame-space `wire` pushed
   *  through this transform (that would start it on a stretched ellipse). */
  wireOnScreen(index: number, agents = 0): Curve {
    const end = this.wireEndOnScreen(index);
    const angle = wireStartAngle(index);
    const start = this.ringPointOnScreen(angle, ringR + wireRingGap(angle, agents));
    const dx = end.x - start.x;
    return { start, c1: { x: start.x + dx * 0.55, y: start.y }, c2: { x: start.x + dx * 0.45, y: end.y }, end };
  }

  /** The centre of rail row `index`'s art in SCREEN points — what its tile is placed by. */
  railIconCentreOnScreen(index: number): Pt {
    return { x: this.x(railIconCx), y: this.y(railCy(index)) };
  }

  /** Where rail row `index`'s wire lands, in SCREEN points: the frame's dot-to-art offset (`wireEndCy − railCy`:
   *  −8, 0, −6, −3, −2, +2, +11) scaled UNIFORMLY off the art's centre, so a vertically stretched band never pulls
   *  a dot off its item. */
  wireEndOnScreen(index: number): Pt {
    const icon = this.railIconCentreOnScreen(index);
    return { x: this.x(wireEndCx), y: icon.y + this.size(wireEndCy(index) - railCy(index)) };
  }

  /** A cluster's wire in SCREEN points, for the same reason as `wireOnScreen`. */
  clusterWireOnScreen(cluster: Cluster, agents = 0, foldedConnectorsWidth?: number | null): Curve {
    const angle = clusterRingAngle(cluster);
    const radius = ringR + wireRingGap(angle, agents);
    const anchor = this.pt(clusterAnchor(cluster, foldedConnectorsWidth));
    const lifted = cluster === 'channels' ? { x: anchor.x, y: Math.min(anchor.y, this.channelsRingCy()) } : anchor;
    return clusterCurve(cluster, lifted, this.ringPointOnScreen(angle, radius));
  }

  /**
   * The channels tray's centre held off the RING (the design's distance above the ring's centre, scaled as a size):
   * its band position follows the height while the ring and the dots follow the width, so a squeezed band brought the
   * tray down onto the agent at the ring's top. The tray and its wire's anchor take whichever is higher, this or the
   * band position; the same at the frame's proportions, which mobile web always has (it sizes the band from the
   * width), so only the phones ever lift it. Mirrors iOS/Android `channelsRingCy`.
   */
  channelsRingCy(): number {
    return this.ringCentre().y - this.size(ringCy - channelsCy);
  }

  /** A tray's top edge on screen: its band position, the channels tray lifted off the ring when squeezed. */
  trayTopOnScreen(cluster: Cluster): number {
    return cluster === 'channels'
      ? Math.min(this.y(channelsCy - trayH / 2), this.channelsRingCy() - this.size(trayH / 2))
      : this.y(connectorsCy - trayH / 2);
  }

  curve(c: Curve): Curve {
    return { start: this.pt(c.start), c1: this.pt(c.c1), c2: this.pt(c.c2), end: this.pt(c.end) };
  }
}

/**
 * The scale that fits the design's map band into `heightPt` of real estate at `widthPt` across. Width leads — the
 * rail must stay pinned to the right edge and the clusters to the left. It squeezes only down to `squeezeFloor`
 * (a shorter band keeps the floor and the phones scroll the board), the same rule on all three surfaces. (Mobile web
 * gives the band the frame's own aspect ratio, so the two scales come out equal there.)
 */
export function scaleTo(widthPt: number, heightPt: number): Scale {
  return new Scale(widthPt / frameW, Math.max(heightPt, minBandHeightAt(widthPt)) / (bandBottom - bandTop), bandTop);
}

/**
 * The most the band's height may shrink against its width (`sy / sx`). The rail's rows are then the tightest spacing
 * (their closest pair is 64 apart for a 40 art and its label), so the floor is the squeeze a phone a little short of
 * the frame already runs at and reads right (an iPhone 17, ≈ 0.96). The channels tray is lifted instead (`channelsRingCy`).
 */
export const squeezeFloor = 0.95;

/** The least band height the board is drawn at, `widthPt` across; a shorter band scrolls (on the phones). */
export function minBandHeightAt(widthPt: number): number {
  return (((bandBottom - bandTop) * widthPt) / frameW) * squeezeFloor;
}
